import React, {useEffect, useState} from 'react';
import 'styles/notification/NotificationPopup.css';
import 'styles/notification/NotificationPanel.css';
import {
    getNotificationsPreviewRequest,
    markAllAsReadRequest,
    notificationReadElusiveRequest,
    resetIgnoredNotificationsRequest
} from 'util/api';
import NotificationPopupItem from 'components/notification/NotificationPopupItem';
import {useNavigate} from 'react-router-dom';
import UniversalOptionsList from 'components/universal/UniversalOptionsList';
import {useNotification} from 'util/NotificationContext';
import confettiService from "../donate/ConfettiService";

const NotificationPopup = ({onHide}) => {
    const [allNotifications, setAllNotifications] = useState([]);
    const [notifications, setNotifications] = useState([]);
    const [isSliced, setIsSliced] = useState(false);
    const [loading, setLoading] = useState(true);
    const [unreadOnly, setUnreadOnly] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    const navigate = useNavigate();
    const {resetMissedNotifications} = useNotification();

    useEffect(() => {
        resetMissedNotifications();
        resetIgnoredNotificationsRequest();
        return () => {
            resetMissedNotifications();
            resetIgnoredNotificationsRequest();
        };
    }, []);

    const notificationClicked = () => {
        onHide();
    };

    useEffect(() => {
        const getAllNotifications = async () => {
            const data = await getNotificationsPreviewRequest();
            const localNotifications = data.notifications
                .sort((a, b) => a.b.createdOn - b.b.createdOn);
            setAllNotifications(localNotifications);
            setUnreadCount(data.unreadCount);
            setLoading(false);
        };

        getAllNotifications();
    }, []);

    useEffect(() => {
        let newNotifications;
        if (unreadOnly) {
            newNotifications = allNotifications.filter((notification) => !notification.b.seen);
        } else {
            newNotifications = allNotifications;
        }
        newNotifications = newNotifications.sort((a, b) => b.b.createdOn - a.b.createdOn);
        setNotifications(newNotifications.slice(0, 5));
        setIsSliced(newNotifications.length >= 5);
    }, [unreadOnly, allNotifications]);

    const onNotificationRead = (id, message) => {
        const localUnreadNotifications = allNotifications.filter((notification) => !notification.b.seen);
        const notificationIdsString = localUnreadNotifications.map(notification => notification.id).join(';');

        notificationReadElusiveRequest(id, notificationIdsString).then((response) => {
            let confettiCount = confettiService.calculateConfettiCount(message);
            confettiService.fire(confettiCount);
            setAllNotifications((prevNotifications) => {
                // Mark the clicked notification as read
                const updatedNotifications = prevNotifications.map((notification) => {
                    if (notification.id === id) {
                        notification.b.seen = true;
                    }
                    return notification;
                });

                // Merge new notifications from response and remove duplicates
                const mergedNotifications = [...response, ...updatedNotifications];
                return mergedNotifications.filter(
                    (notification, index, self) =>
                        index === self.findIndex((n) => n.id === notification.id)
                );
            });

            // Update unread count
            setUnreadCount((prevUnreadCount) => prevUnreadCount - 1);
        });
    };

    const handleUnreadOnly = (option) => {
        if (option === 'unread') {
            setUnreadOnly(true);
        } else {
            setUnreadOnly(false);
        }
    };

    const clickedShowMore = () => {
        onHide();
        navigate('/notifications');
    };

    const markAllAsRead = () => {
        markAllAsReadRequest().then((response) => {
            let confettiCount = 0;
            response.forEach((message) => {
                confettiCount += confettiService.calculateConfettiCount(message);
            });
            confettiService.fire(confettiCount);

            setAllNotifications((prevNotifications) => prevNotifications.map((notification) => ({
                ...notification,
                b: {
                    ...notification.b,
                    seen: true
                }
            })));
            setUnreadCount(0);
        });
    };

    return (
        <>
            <div className="notification-bubble-header">
                <UniversalOptionsList onOptionClick={handleUnreadOnly} options={['all', 'unread']}
                                      initialOption={'all'}/>
                <div onClick={markAllAsRead} className="notification-bubble-all-as-read">
                    Mark all as read ({unreadCount})
                </div>
                <div className="notification-bubble-enlarge" onClick={clickedShowMore}>
                    <img src="/images/enlarge.svg" alt="enlarge" width="20px"/>
                </div>
            </div>
            {notifications.map((notification) => (
                <NotificationPopupItem
                    notification={notification}
                    onClick={notificationClicked}
                    onNotificationRead={onNotificationRead}
                />
            ))}
            {isSliced && (
                <div className="all-notifications-in-bubble" onClick={clickedShowMore}>
                    All notifications
                </div>
            )}

            {loading && (
                <div className="no-notifications-in-bubble">
                    Loading...
                </div>
            )}

            {notifications.length === 0 && !loading && (
                <div className="no-notifications-in-bubble">
                    No {unreadOnly ? 'unread ' : ''}notifications
                </div>
            )}
        </>
    );
};

export default NotificationPopup;